import { useEffect, useState } from "react";
import { toast } from "sonner";
import { submitInquiry, fetchProducts } from "../lib/api";
import { Send, CheckCircle2 } from "lucide-react";

export default function QuoteForm({ defaultProduct = "", compact = false }) {
  const [products, setProducts] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    product: defaultProduct,
    message: "",
  });

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
  }, []);

  useEffect(() => {
    if (defaultProduct) setForm((f) => ({ ...f, product: defaultProduct }));
  }, [defaultProduct]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill name, email, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry(form);
      setSubmitted(true);
      toast.success("Inquiry received — we'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", company: "", product: defaultProduct, message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong, please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="quote-form-success" className="bg-[var(--bg-secondary)] border border-[var(--border-subtle)] p-8 md:p-12 text-center">
        <CheckCircle2 className="mx-auto text-[var(--maroon-700)]" size={48} />
        <h3 className="font-display text-2xl md:text-3xl mt-4 text-[var(--maroon-950)]">Inquiry received</h3>
        <p className="mt-3 text-[var(--text-secondary)] max-w-md mx-auto">
          Thank you. Our team will review your requirement and respond within one business day.
        </p>
        <button
          data-testid="quote-form-reset"
          onClick={() => setSubmitted(false)}
          className="btn-secondary mt-6"
        >
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="quote-form"
      onSubmit={onSubmit}
      className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-x-8 gap-y-7`}
    >
      <div>
        <label className="field-label" htmlFor="qf-name">Your name</label>
        <input id="qf-name" data-testid="qf-name" className="field-input" placeholder="Indermeet Singh" value={form.name} onChange={update("name")} />
      </div>
      <div>
        <label className="field-label" htmlFor="qf-company">Company / Bakery</label>
        <input id="qf-company" data-testid="qf-company" className="field-input" placeholder="Optional" value={form.company} onChange={update("company")} />
      </div>
      <div>
        <label className="field-label" htmlFor="qf-email">Email</label>
        <input id="qf-email" data-testid="qf-email" type="email" className="field-input" placeholder="you@company.com" value={form.email} onChange={update("email")} />
      </div>
      <div>
        <label className="field-label" htmlFor="qf-phone">Phone / WhatsApp</label>
        <input id="qf-phone" data-testid="qf-phone" className="field-input" placeholder="+91 98XXX XXXXX" value={form.phone} onChange={update("phone")} />
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <label className="field-label" htmlFor="qf-product">Product of interest</label>
        <select
          id="qf-product"
          data-testid="qf-product"
          className="field-input"
          value={form.product}
          onChange={update("product")}
        >
          <option value="">Select a product (optional)</option>
          {products.map((p) => (
            <option key={p.slug} value={p.name}>{p.name}</option>
          ))}
          <option value="Other / Custom">Other / Custom</option>
        </select>
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <label className="field-label" htmlFor="qf-message">Tell us about your requirement</label>
        <textarea
          id="qf-message"
          data-testid="qf-message"
          className="field-input"
          rows={4}
          placeholder="Production line, biscuit type, desired imprint, quantities, timeline..."
          value={form.message}
          onChange={update("message")}
        />
      </div>
      <div className={compact ? "" : "md:col-span-2"}>
        <button
          data-testid="qf-submit"
          type="submit"
          disabled={loading}
          className="btn-primary w-full md:w-auto disabled:opacity-60"
        >
          {loading ? "Submitting..." : (<>Submit Inquiry <Send size={16} /></>)}
        </button>
        <p className="mt-4 text-xs text-[var(--text-muted)]">
          By submitting you agree to be contacted regarding your inquiry. We do not share your details.
        </p>
      </div>
    </form>
  );
}
